import React from 'react';

import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Popper from '@mui/material/Popper';
import TextField from '@mui/material/TextField';
import { VariableSizeList } from 'react-window';

import type { SxProps } from '@mui/material/styles';
import type { ListChildComponentProps } from 'react-window';

export interface VirtualizedMultiSelectProps<T> {
  options: T[];
  value: T[];
  onChange: (value: T[]) => void;
  styles?: SxProps;
  error?: boolean;
  errorStyles?: SxProps;
  loading?: boolean;
  loadingText?: string;
  label?: string;
  getOptionLabel: (option: T) => string;
  isOptionEqualToValue?: (option: T, value: T) => boolean;
  dataTestId?: string;
}

const LISTBOX_PADDING = 8; // px

function renderRow(props: ListChildComponentProps) {
  const { data, index, style } = props;
  return React.cloneElement(data[index], {
    style: {
      ...style,
      top: (style.top as number) + LISTBOX_PADDING
    }
  });
}

const OuterElementContext = React.createContext({});

const OuterElementType = React.forwardRef<HTMLDivElement>((props, ref) => {
  const outerProps = React.useContext(OuterElementContext);
  return <div ref={ref} {...props} {...outerProps} />;
});
OuterElementType.displayName = 'OuterElementType';

function useResetCache(data: any) {
  const ref = React.useRef<VariableSizeList>(null);
  React.useEffect(() => {
    if (ref.current != null) {
      ref.current.resetAfterIndex(0, true);
    }
  }, [data]);
  return ref;
}

const ListboxComponent = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLElement>>(
  (props, ref) => {
    const { children, ...other } = props;
    const itemData = React.Children.toArray(children);
    const itemCount = itemData.length;
    const itemSize = 48;
    const getChildSize = () => itemSize;
    const getHeight = () => (itemCount > 8 ? 8 * itemSize : itemCount * itemSize);
    const gridRef = useResetCache(itemCount);

    return (
      <div ref={ref}>
        <OuterElementContext.Provider value={other}>
          <VariableSizeList
            ref={gridRef}
            height={getHeight() + 2 * LISTBOX_PADDING}
            innerElementType="ul"
            itemCount={itemCount}
            itemData={itemData}
            itemSize={getChildSize}
            outerElementType={OuterElementType}
            overscanCount={5}
            width="100%"
          >
            {renderRow}
          </VariableSizeList>
        </OuterElementContext.Provider>
      </div>
    );
  }
);

const CustomPopper = (props: any) => <Popper {...props} placement="top-start" />;

const VirtualizedMultiSelect = <T,>({
  options,
  value,
  onChange,
  styles,
  error,
  errorStyles,
  loading,
  loadingText,
  label,
  getOptionLabel,
  isOptionEqualToValue,
  dataTestId = 'virtualized-multi-select'
}: VirtualizedMultiSelectProps<T>) => (
  <Box sx={{ position: 'relative' }}>
    <Autocomplete
      disableListWrap
      disablePortal
      multiple
      data-testid={dataTestId}
      getOptionLabel={getOptionLabel}
      id={dataTestId}
      isOptionEqualToValue={isOptionEqualToValue}
      loading={loading}
      loadingText={loadingText}
      onChange={(_event, newValue) => onChange(newValue)}
      options={options}
      sx={styles}
      value={value}
      renderInput={(params) => (
        <TextField
          {...params}
          error={error}
          label={label}
          slotProps={{ inputLabel: { shrink: true } }}
        />
      )}
      slotProps={{
        listbox: {
          component: ListboxComponent
        },
        popper: {
          component: CustomPopper
        }
      }}
    />
    {error ? <Box sx={errorStyles}>This field is required</Box> : null}
  </Box>
);

export { VirtualizedMultiSelect };
